import { Component, OnInit, OnDestroy, HostBinding, HostListener } from '@angular/core';
import { Router, NavigationEnd, ActivationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge, take } from 'rxjs';
import { delay, filter, map, switchMap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import { environment } from '@env/environment';
import { Logger, I18nService, untilDestroyed, CredentialsService, versionChange$, UrlParamsService, UrlParams, StorageService } from '@app/core';
import { AppService, ErrorHandlerService, GoogleAnalyticsService, IntercomService, UserService, WhitelabelService, WootricService } from '@app/services';
import { VersionUpdateModalService } from '@app/shared';

const log = new Logger('App');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly urlParamsStorageKey = 'tmpUrlParams';

  @HostBinding('class.darkmode') get isDarkmode() {
    return this.appService.isDarkmode;
  }

  constructor(
    private router: Router,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private storageService: StorageService,
    private credentialsService: CredentialsService,
    private appService: AppService,
    private userService: UserService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private intercomService: IntercomService,
    private wootricService: WootricService,
    private versionUpdateModalService: VersionUpdateModalService,
    private whitelabelService: WhitelabelService,
    private urlParamsService: UrlParamsService,
    private errorHandlerService: ErrorHandlerService
  ) {}

  ngOnInit() {
    Sentry.setUser({ ip_address: '{{auto}}' });

    // Setup logger
    if (environment.production && !environment.debug) {
      Logger.enableProductionMode();
    }
    log.debug('init');

    if (environment.production) {
      this.googleAnalyticsService.startTracking();

      if (!this.appService.isDocumentPage() && this.appService.isAutentiqueDomain() && window.innerWidth > 915 && !this.appService.isInsideIframe()) {
        this.intercomService.startTracking();
      } else {
        this.intercomService.hide();
      }
    }

    if (!this.appService.isAutentiqueDomain()) {
      this.whitelabelService.loadWhitelabelByDomain().subscribe({
        next: whitelabel => {
          this.whitelabelService.isApplicationWhitelabelLoaded = true;
        },
        // If an error happens it'd render a blank page. The following code at least shows something, while also not showing a whitelabel-less page to the user
        error: () => this.router.navigate(['/401'], { replaceUrl: true })
      });
    } else {
      this.whitelabelService.resetWhitelabelInfo();
      this.whitelabelService.setMetaTags();
      this.whitelabelService.isApplicationWhitelabelLoaded = true;
    }

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    // Change page title on navigation or language change, based on route data
    const onActivationEnd$ = this.router.events.pipe(filter(event => event instanceof ActivationEnd));
    merge(this.translateService.onLangChange, onActivationEnd$)
      .pipe(
        map((route: ActivationEnd) => route?.snapshot?.data),
        untilDestroyed(this)
      )
      .subscribe({
        next: event => {
          const title = event?.title;
          if (title) {
            this.titleService.setTitle(this.translateService.instant(title));
          }
        }
      });

    // Sets previous and current url
    const onNavigationEnd$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));
    onNavigationEnd$.subscribe({
      next: (event: NavigationEnd) => this.appService.setNewUrl(event.url)
    });

    if (window.electronAPI?.onOpenUrl) {
      electronAPI.onOpenUrl((url: string) => (url ? this.router.navigate([url]) : null));
    }

    versionChange$
      .pipe(
        delay(500),
        switchMap(version => this.versionUpdateModalService.open().pipe(map(() => version)))
      )
      .subscribe({
        next: version => (window.location.href = `${window.location.href}${window.location.href.match(/\?.*$/) ? '&' : '?'}v=${version}`)
      });

    const navigationEnd$ = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd));

    // setup url params
    navigationEnd$.subscribe({
      next: () => {
        this.urlParamsService.params = this.router.parseUrl(this.router.url).queryParams as UrlParams;
        if (this.urlParamsService.dirtyParams.size === 0) {
          this.urlParamsService.params = this.storageService.getJSON(this.urlParamsStorageKey) || {};
        }
        this.appService.listFormat = this.urlParamsService.isParamDirty('listType') ? this.urlParamsService.params.listType : this.appService.listFormat;
      }
    });

    navigationEnd$
      .pipe(
        take(1),
        switchMap(() => this.userService.watchCurrentUser()),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          if (environment.production && this.intercomService.isBooted && user) {
            this.intercomService.setUserProperties(user);
          }

          if (user) {
            Sentry.setUser({ id: user.id, username: user.name, email: user.email, phone: user.phone, ip_address: '{{auto}}' });
            this.appService.refreshCreditsPrice();
          }

          if (user && environment.production && this.appService.isAutentiqueDomain() && !this.appService.isDocumentPage() && !this.appService.isInsideIframe()) {
            this.wootricService.boot(user);
          }
          if (this.credentialsService.decodedAccessToken?.organization_id) {
            this.appService.isEnterpriseChild = true;
          }
        },
        error: error => {
          if (error && error.message && !this.appService.isTokenInvalidError(error.message)) {
            this.errorHandlerService.handle(error);
          }

          this.credentialsService.clearCredentials();
          if (!this.appService.isDocumentPage()) {
            this.router.navigate(['/entrar'], { replaceUrl: true });
          }
        }
      });
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: Event) {
    // Makes sure to remember URL parameters when refreshing the page, even when after they were removed from the URL
    if (this.urlParamsService.dirtyParams.size > 0) {
      this.storageService.setJSON(
        this.urlParamsStorageKey,
        Object.fromEntries(Object.entries(this.urlParamsService.params).filter(([key]) => this.urlParamsService.isParamDirty(key as keyof UrlParams))),
        true
      );
    }
  }
}
